import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { CommunicationService, sharedApiClient } from '../fetcher';
import { useNotifications, todayCalendarDate, calendarDateToEpochMillis } from '@medprompt/shared';
import { ConsultationStatusEnum } from '../enums/consultationStatusEnum';

const useConsultationFileAway = ({ consultationId, consultation, consultationService, updateConsultation }) => {
  const { addNotification } = useNotifications();
  const communicationService = useMemo(() => new CommunicationService(sharedApiClient), []);

  const [comms, setComms] = useState([]);
  const [fileAwayLoading, setFileAwayLoading] = useState(false);
  const [showFileAwayModal, setShowFileAwayModal] = useState(false);

  const consultationIdRef = useRef(consultationId);
  useEffect(() => {
    consultationIdRef.current = consultationId;
    setShowFileAwayModal(false);
    setFileAwayLoading(false);
    setComms([]);
  }, [consultationId]);

  const hasConsultation = Boolean(consultation);

  const handleFileAway = useCallback(async () => {
    if (!hasConsultation || fileAwayLoading) return;
    const targetId = consultationId;

    let consultationComms = [];
    try {
      consultationComms = (await communicationService.fetchConsultationComms(targetId)) || [];
    } catch (error) {
      console.error('Failed to fetch consultation comms:', error);
    }
    // Comms for a consultation the user has since left must not open its modal on this one.
    if (targetId !== consultationIdRef.current) return;

    setComms(consultationComms);
    setShowFileAwayModal(true);
  }, [hasConsultation, fileAwayLoading, consultationId, communicationService]);

  const handleFileAwayConfirm = useCallback((fileAwayDate, shouldIncludeTasks, shouldIncludeComms) => {
    if (!hasConsultation || fileAwayLoading) return;
    const targetId = consultationId;

    setFileAwayLoading(true);

    const todayString = todayCalendarDate();
    let dateParam = null;
    if (fileAwayDate !== todayString) {
      dateParam = calendarDateToEpochMillis(fileAwayDate);
    }

    consultationService.fileAwayConsultation(targetId, dateParam, shouldIncludeTasks, shouldIncludeComms)
      .then(() => {
        // The response only confirms the consultation was queued, so reflect that locally rather
        // than waiting for the status event: it is what disables editing and a second File Away.
        updateConsultation(prevConsultation => (
          prevConsultation && prevConsultation.consultationId === targetId
            ? { ...prevConsultation, status: ConsultationStatusEnum.QUEUED_FOR_FILING.value }
            : prevConsultation
        ));
        setFileAwayLoading(false);
        setShowFileAwayModal(false);
      }).catch((error) => {
        console.error('Error filing away consultation:', error);
        addNotification(`file-away-error-${targetId}`, 'Failed to file away consultation', { isError: true });
        setFileAwayLoading(false);
        setShowFileAwayModal(false);
      });
  }, [hasConsultation, fileAwayLoading, consultationId, consultationService, updateConsultation, addNotification]);

  const handleFileAwayCancel = useCallback(() => {
    setShowFileAwayModal(false);
  }, []);

  return {
    comms,
    fileAwayLoading,
    showFileAwayModal,
    handleFileAway,
    handleFileAwayConfirm,
    handleFileAwayCancel,
  };
};

export default useConsultationFileAway;
